'use client'

import { useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'

interface SelectOption {
  value: string
  label: string
  price: number
}

interface SimplifiedSelectorProps {
  type: 'credits' | 'domains'
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  isAnnual: boolean
  recommendedValue?: string
  plan: 'basic' | 'premium' | 'platinum'
}

/**
 * Super simplified selector - show base + simple add-on buttons
 * Much easier to understand and convert
 */
export function SimplifiedSelector({
  type,
  options,
  value,
  onChange,
  isAnnual,
  recommendedValue,
  plan,
}: SimplifiedSelectorProps) {
  const t = useTranslations('pricing-v3')
  const [showMore, setShowMore] = useState(false)

  // Determine recommended value based on plan
  const getRecommendedValue = (): string => {
    if (recommendedValue) return recommendedValue

    if (type === 'credits') {
      switch (plan) {
        case 'basic':
          return '200'
        case 'premium':
          return '500'
        case 'platinum':
          return '1000'
        default:
          return '200'
      }
    } else {
      // domains
      switch (plan) {
        case 'basic':
          return '1'
        case 'premium':
          return '3'
        case 'platinum':
          return '10'
        default:
          return '1'
      }
    }
  }

  const recommended = getRecommendedValue()
  const selectedOption = options.find((opt) => opt.value === value)
  const recommendedOption = options.find((opt) => opt.value === recommended)

  // Only show "Need more?" for add-ons (options with price > 0)
  const addOnOptions = options.filter((opt) => opt.value !== recommended)

  return (
    <div className="space-y-3">
      {/* Show base amount clearly */}
      <div>
        <div className="text-lg font-semibold text-white">
          {selectedOption?.label.split('(')[0].trim()}
        </div>
        {selectedOption && selectedOption.price > 0 && (
          <div className="text-sm text-white/60 mt-1">
            {selectedOption.label.includes('(') &&
              `(${selectedOption.label.split('(')[1]}`}
          </div>
        )}
      </div>

      {/* Simple add-on buttons - only if not using base */}
      {value === recommended && addOnOptions.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm text-white/60">
            {type === 'credits' ? 'Need more credits?' : 'Need more domains?'}
          </div>
          <div className="space-y-2">
            {addOnOptions.slice(0, showMore ? undefined : 3).map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-left"
              >
                <span className="text-sm text-white flex items-center gap-2">
                  <Plus className="w-4 h-4 text-green-400" />
                  {option.label.split('(')[0].trim()}
                </span>
                <span className="text-sm font-medium text-white">
                  ${option.price}
                </span>
              </button>
            ))}
            {addOnOptions.length > 3 && !showMore && (
              <button
                type="button"
                onClick={() => setShowMore(true)}
                className="w-full text-sm text-purple-400 hover:text-purple-300 py-2"
              >
                Show more options...
              </button>
            )}
          </div>
        </div>
      )}

      {/* Show selected add-on if any */}
      {value !== recommended && selectedOption && (
        <div className="space-y-2">
          <div className="bg-purple-500/20 border border-purple-500/40 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white">
                {selectedOption.label.split('(')[0].trim()}
              </span>
              <span className="text-sm font-medium text-white">
                +${selectedOption.price}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(recommended)}
            className="text-sm text-purple-400 hover:text-purple-300"
          >
            ← Back to base plan
          </button>
        </div>
      )}
    </div>
  )
}
